/**
 * Copyright (C) 2022 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import React, { useCallback } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';

import keyBy from 'lodash/fp/keyBy';
import sortBy from 'lodash/fp/sortBy';
import filterBy from 'lodash/fp/filter';
import sortedUniqBy from 'lodash/fp/sortedUniqBy';
import { selectTerminalFilters, selectTerminalVisibilityToggleState, selectTerminals } from '../../store';
import FilterSelect, { FilterSelectItem } from '../common/filter/FilterSelect';
import Terminal from '../../shared/entities/terminal';
import {
  toggleTerminalFilters,
  showAllTerminalsInPlatformList,
  hideAllTerminalsInPlatformList,
  closeDetailsPanel,
} from '../../store/ui/actions';
import { isOnline } from '../../utils/ConnectionStatusUtils';
import { clearFlightPath } from '../../store/terminals/actions';
import FilterList from '../common/filter/FilterList';
import { TERMINAL_PLATFORMS } from '../../shared/entities/terminalPlatform';
import { TerminalVisibilityToggleState } from '../../store/ui/reducer';
import Button from '@mui/material/Button';

import appTheme from '../common/theme/appTheme.json';

const keyByKey = keyBy<FilterSelectItem>('key');

const makeGetUniqueTerminalsFunction = (key: 'beamDisplayName' | 'networkOrigin' | 'platformType') => {
  const uniq = sortedUniqBy<Terminal>(key);
  const filter = filterBy<Terminal>(key);
  const sort = sortBy<Terminal>(key);

  return (terminals: { [key: string]: Terminal }): Terminal[] => uniq(filter(sort(terminals)));
};

const getBeamTerminals = makeGetUniqueTerminalsFunction('beamDisplayName');
const getNetworkTerminals = makeGetUniqueTerminalsFunction('networkOrigin');
const getPlatformTerminals = makeGetUniqueTerminalsFunction('platformType');

const mapToBeamItems =
  (parent: FilterSelectItem) =>
  (terminal: Terminal): FilterSelectItem => {
    return {
      key: terminal.beamDisplayName,
      label: terminal.beamDisplayName,
      path: [...parent.path, terminal.beamDisplayName],
      parentKey: parent.key,
      parentLabel: parent.label,
    };
  };

const mapToNetworkItems =
  (parent: FilterSelectItem) =>
  (terminal: Terminal): FilterSelectItem => {
    return {
      key: terminal.networkOrigin,
      label: terminal.networkOrigin,
      path: [...parent.path, terminal.networkOrigin],
      parentKey: parent.key,
      parentLabel: parent.label,
    };
  };

export interface FleetViewHeaderContentsProps {}

const FleetViewHeaderContents = (props: FleetViewHeaderContentsProps) => {
  const dispatch = useDispatch();

  const filters = useSelector(selectTerminalFilters, shallowEqual);
  const terminals = useSelector(selectTerminals, shallowEqual);
  const terminalVisibilityToggleState = useSelector(selectTerminalVisibilityToggleState, shallowEqual);

  const mapToPlatformItems = useCallback(
    (parent: FilterSelectItem) =>
      (terminal: Terminal): FilterSelectItem => {
        const label = TERMINAL_PLATFORMS[terminal.platformType].defaultDisplayName;

        return {
          key: terminal.platformType,
          label,
          path: [...parent.path, terminal.platformType],
          parentKey: parent.key,
          parentLabel: parent.label,
        };
      },
    [],
  );

  const getMenu = useCallback((): FilterSelectItem => {
    const uniqueBeamTerminals = getBeamTerminals(terminals);
    const uniqueNetworkTerminals = getNetworkTerminals(terminals);
    const uniquePlatformTerminals = getPlatformTerminals(terminals);

    const beamDisplayName: FilterSelectItem = {
      label: 'Beams',
      key: 'beamDisplayName',
      path: ['fleet', 'beamDisplayName'],
    };

    const networkOrigin: FilterSelectItem = {
      label: 'Networks',
      key: 'networkOrigin',
      path: ['fleet', 'networkOrigin'],
    };

    const platformType: FilterSelectItem = {
      label: 'Platforms',
      key: 'platformType',
      path: ['fleet', 'platformType'],
    };

    beamDisplayName.children = keyByKey(uniqueBeamTerminals.map(mapToBeamItems(beamDisplayName)));
    networkOrigin.children = keyByKey(uniqueNetworkTerminals.map(mapToNetworkItems(networkOrigin)));
    platformType.children = keyByKey(uniquePlatformTerminals.map(mapToPlatformItems(platformType)));

    return {
      label: 'Filter Fleet By',
      key: 'fleet',
      path: ['fleet'],
      children: {
        beamDisplayName,
        networkOrigin,
        platformType,
      },
    };
  }, [terminals, mapToPlatformItems]);

  const toggleTerminalVisibility = (): void => {
    if (terminalVisibilityToggleState === TerminalVisibilityToggleState.SHOW_ALL && terminals) {
      dispatch(hideAllTerminalsInPlatformList());
      dispatch(closeDetailsPanel());
      dispatch(clearFlightPath());
    } else if (terminalVisibilityToggleState === TerminalVisibilityToggleState.HIDE_ALL) {
      dispatch(showAllTerminalsInPlatformList());
    }
  };

  // Calculates the number of online platforms - accumulates on an object of online terminals in order to avoid adding multiple terminals from the same aircraft.
  const count = Object.keys(
    Object.values(terminals).reduce((accumulatedTerminals: { [datapathUuid: string]: Terminal }, terminal: Terminal) => {
      if (
        accumulatedTerminals[terminal.datapathUuid] ||
        (terminal.datapathUuidsOnAircraft || []).some(
          (datapathUuidOnAircraft: string) => accumulatedTerminals[datapathUuidOnAircraft],
        )
      ) {
        return { ...accumulatedTerminals };
      } else {
        return { ...accumulatedTerminals, [terminal.datapathUuid]: terminal };
      }
    }, {} as { [datapathUuid: string]: Terminal }),
  ).length;
  const onlineCount = Object.keys(
    Object.values(terminals).reduce((accumulatedTerminals: { [datapathUuid: string]: Terminal }, terminal: Terminal) => {
      if (
        accumulatedTerminals[terminal.datapathUuid] ||
        (terminal.datapathUuidsOnAircraft || []).some(
          (datapathUuidOnAircraft: string) => accumulatedTerminals[datapathUuidOnAircraft],
        )
      ) {
        return { ...accumulatedTerminals };
      } else if (!isOnline(terminal.connectivityStatus)) {
        return { ...accumulatedTerminals };
      } else {
        return { ...accumulatedTerminals, [terminal.datapathUuid]: terminal };
      }
    }, {} as { [datapathUuid: string]: Terminal }),
  ).length;
  const menu = getMenu();

  const setFilter = (item: FilterSelectItem) => {
    const itemKey = item.path[item.path.length - 1];
    const parentKey = item.path[item.path.length - 2];
    dispatch(toggleTerminalFilters({ key: parentKey, value: itemKey }));
  };

  return (
    <React.Fragment>
      <h1>
        My Fleet
        <span data-test="platform__header__platform__count" className="viewheader__count" id="viewheader_count">
          {` (${count} • ${onlineCount} Online)`}
        </span>
      </h1>
      <Button
        variant="text"
        onClick={toggleTerminalVisibility}
        sx={{
          width: '81px',
          marginLeft: '16px',
          color: appTheme.buttonSelectedColor,
          ':hover': { backgroundColor: '#1976d20a' },
        }}
      >
        {terminalVisibilityToggleState === TerminalVisibilityToggleState.HIDE_ALL ? 'SHOW ALL' : 'HIDE ALL'}
      </Button>
      <FilterSelect menu={menu} filters={filters} onSelect={setFilter} />
      <FilterList menu={menu} filters={filters} onSelect={setFilter} />
    </React.Fragment>
  );
};

export default FleetViewHeaderContents;
